import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeftIcon, CheckCircleIcon, DocumentTextIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';

// IRS W-4 — inline form. Collects Steps 1-4 plus typed signature; on
// submit the server fills the official IRS W-4 AcroForm and flattens.
// Most candidates only need the basics — Step 4 (extra adjustments) is
// optional and defaults to zero.

export default function MyW4Page() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);

  const [form, setForm] = useState({
    firstName: '', middleInitial: '', lastName: '',
    ssn: '',
    address: '', city: '', state: '', zip: '',
    filingStatus: 'single',
    multipleJobs: false,
    qualifyingChildren: '',
    otherDependents: '',
    otherIncome: '',
    deductions: '',
    extraWithholding: '',
    signature: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/my/w4')
      .then(({ data }) => {
        setCtx(data);
        // Prefill name from the portal profile.
        setForm(prev => ({
          ...prev,
          firstName: prev.firstName || data.first_name || '',
          lastName: prev.lastName || data.last_name || '',
        }));
      })
      .catch(() => toast.error('Could not load your profile.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-400">Loading…</div></div>;
  }
  if (!ctx) return null;

  const fullName = `${ctx.first_name} ${ctx.last_name}`.trim();
  const alreadyDone = ctx.step_status === 'completed' || ctx.step_status === 'verified';
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.signature.trim()) return toast.error('Type your signature.');
    if (form.signature.trim().toLowerCase() !== fullName.toLowerCase()) {
      return toast.error(`Signature must match your name exactly: "${fullName}".`);
    }
    setSubmitting(true);
    try {
      await api.post('/my/w4', form);
      toast.success('W-4 submitted!');
      setTimeout(() => navigate('/my/onboarding'), 800);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not submit W-4.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toaster position="top-right" />
      <div className="max-w-2xl mx-auto">
        <Link to="/my/onboarding" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeftIcon className="w-4 h-4" /> Back to onboarding
        </Link>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-orange-50 border-b border-orange-100 p-5 flex items-center gap-3">
            <BanknotesIcon className="w-6 h-6 text-orange-500" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">IRS Form W-4</h1>
              <p className="text-xs text-gray-500 mt-0.5">Employee's Withholding Certificate. Fill in Steps 1, 3-4 (Step 2 is rarely needed).</p>
            </div>
          </div>

          {alreadyDone ? (
            <div className="p-8 space-y-4">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircleIcon className="w-6 h-6" />
                <p className="font-medium">W-4 already submitted</p>
              </div>
              <p className="text-sm text-gray-600">
                You sent in your W-4 on{' '}
                {ctx.completed_at ? new Date(ctx.completed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'an earlier date'}.
                If you need to change it, contact your admin to reset this step.
              </p>
              {ctx.document_url && (
                <a href={ctx.document_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:underline">
                  <DocumentTextIcon className="w-4 h-4" /> View submitted PDF
                </a>
              )}
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* ─── Step 1 ────────────────────────────────────────── */}
              <Section title="Step 1 — Personal information">
                <div className="grid grid-cols-12 gap-3">
                  <Field className="col-span-6" label="First name">
                    <Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
                  </Field>
                  <Field className="col-span-2" label="MI">
                    <Input value={form.middleInitial} maxLength={2} onChange={(e) => set('middleInitial', e.target.value)} />
                  </Field>
                  <Field className="col-span-4" label="Last name">
                    <Input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
                  </Field>
                  <Field className="col-span-12" label="Social Security Number">
                    <Input value={form.ssn} inputMode="numeric" maxLength={11}
                      onChange={(e) => set('ssn', e.target.value)} placeholder="123-45-6789" />
                  </Field>
                  <Field className="col-span-12" label="Address">
                    <Input value={form.address} onChange={(e) => set('address', e.target.value)} />
                  </Field>
                  <Field className="col-span-7" label="City">
                    <Input value={form.city} onChange={(e) => set('city', e.target.value)} />
                  </Field>
                  <Field className="col-span-2" label="State">
                    <Input value={form.state} maxLength={2} onChange={(e) => set('state', e.target.value.toUpperCase())} placeholder="IN" />
                  </Field>
                  <Field className="col-span-3" label="ZIP">
                    <Input value={form.zip} inputMode="numeric" maxLength={10} onChange={(e) => set('zip', e.target.value)} />
                  </Field>
                </div>
                <fieldset className="mt-3">
                  <legend className="text-xs font-medium text-gray-700 mb-1">Filing status</legend>
                  <div className="space-y-1.5">
                    {[
                      ['single', 'Single or Married filing separately'],
                      ['married_joint', 'Married filing jointly or Qualifying surviving spouse'],
                      ['head_of_household', 'Head of household'],
                    ].map(([val, label]) => (
                      <label key={val} className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="radio" name="filingStatus" value={val}
                          checked={form.filingStatus === val}
                          onChange={(e) => set('filingStatus', e.target.value)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </Section>

              {/* ─── Step 2 ────────────────────────────────────────── */}
              <Section title="Step 2 — Multiple jobs or spouse works (optional)">
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="mt-0.5"
                    checked={form.multipleJobs}
                    onChange={(e) => set('multipleJobs', e.target.checked)} />
                  <span>I hold more than one job, or my spouse works. (Check this only if there are exactly two jobs in the household — see IRS instructions for higher accuracy options.)</span>
                </label>
              </Section>

              {/* ─── Step 3 ────────────────────────────────────────── */}
              <Section title="Step 3 — Claim dependent credits (optional)">
                <p className="text-xs text-gray-500 mb-2">
                  Enter the number of dependents. The PDF will calculate the dollar amounts:
                  qualifying children × $2,000, other dependents × $500.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Qualifying children under 17">
                    <Input value={form.qualifyingChildren} inputMode="numeric"
                      onChange={(e) => set('qualifyingChildren', e.target.value.replace(/\D/g, ''))} placeholder="0" />
                  </Field>
                  <Field label="Other dependents">
                    <Input value={form.otherDependents} inputMode="numeric"
                      onChange={(e) => set('otherDependents', e.target.value.replace(/\D/g, ''))} placeholder="0" />
                  </Field>
                </div>
              </Section>

              {/* ─── Step 4 ────────────────────────────────────────── */}
              <Section title="Step 4 — Other adjustments (optional)">
                <div className="grid grid-cols-1 gap-3">
                  <Field label="(a) Other income (annual, not from jobs)">
                    <Input value={form.otherIncome} inputMode="decimal"
                      onChange={(e) => set('otherIncome', e.target.value)} placeholder="0" />
                  </Field>
                  <Field label="(b) Deductions">
                    <Input value={form.deductions} inputMode="decimal"
                      onChange={(e) => set('deductions', e.target.value)} placeholder="0" />
                  </Field>
                  <Field label="(c) Extra withholding (per pay period)">
                    <Input value={form.extraWithholding} inputMode="decimal"
                      onChange={(e) => set('extraWithholding', e.target.value)} placeholder="0" />
                  </Field>
                </div>
              </Section>

              {/* ─── Step 5 — Signature ────────────────────────────── */}
              <Section title="Step 5 — Sign and certify">
                <p className="text-[11px] text-gray-500 mb-2">
                  Under penalties of perjury, I declare that this certificate, to the best of my knowledge and belief, is true, correct, and complete.
                </p>
                <p className="text-[11px] text-gray-500 mb-2">
                  Type your full name exactly as <strong>{fullName}</strong> to sign:
                </p>
                <Input value={form.signature} placeholder={fullName}
                  onChange={(e) => set('signature', e.target.value)} />
              </Section>

              <button onClick={submit} disabled={submitting}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white font-semibold py-3 rounded-xl">
                {submitting ? 'Submitting…' : 'Submit W-4'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-200 p-4 bg-gray-50/40">
      <h3 className="text-sm font-semibold text-orange-600 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className={`w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500 ${props.className || ''}`}
    />
  );
}
